import {action, makeObservable, observable} from 'mobx';
import {HomepageItemData, setHomepageData} from '../api';
import {DefaultGroupsIDs, HomepageGroupType} from '../enums';
import {setCollapsedGroupsData} from '../actions/collapsedGroups';
import {createPortletGroup, createShortcutGroup, getGroupId} from '../utils';
import {getUuid4} from '../../../utils/getUuid4';

export type Group = {
    exec: string;
    title: string;
    id: string;
    type: HomepageGroupType;
    items: Array<HomepageItemData & {isHidden?: boolean}>;
    isHidden?: boolean;
};

export enum HomepageDialog {
    addTile = 'addTile',
    addPortlet = 'addPortlet',
    addGroup = 'addGroup',
    removeGroup = 'removeGroup',
    copyHomepage = 'copyHomepage',
    addToHomePage = 'addToHomePage'
}

export class HomepageStore {
    constructor() {
        makeObservable(this);
    }

    @observable items = {};
    @observable scrollToParams: {id: string; blockPosition: 'start' | 'end' | 'nearest'} | null =
        null;
    @observable groups: Map<string, Group> = new Map();
    @observable selectedGroup: string | null = null;

    @observable showDialog = false;
    @observable dialogType: HomepageDialog | null = null;
    @observable addedItemFromMenu: {exec: string; title: string} = null;

    @observable portlets = [];
    @observable updateHash = getUuid4();

    collapsedGroups = [];

    setCollapsedGroups = (groups: string[]) => {
        this.collapsedGroups = groups;
    };

    @action
    setItems(data: Array<HomepageItemData>) {
        this.items = data.reduce(
            (acc, item) => ({
                ...acc,
                [item.exec || item.title]: item
            }),
            {}
        );
        this.groups = this.mapGroups(data);
    }

    @action
    setScrollToParams = (id: string | null, blockPosition?: 'start' | 'end' | 'nearest') => {
        if (id) {
            this.scrollToParams = {id, blockPosition: blockPosition || 'nearest'};
        }
        if (id === null) {
            this.scrollToParams = null;
        }
    };

    @action
    mapGroups(items: Array<HomepageItemData>) {
        let result = new Map();
        let currentGroup = null;

        const portletGroup = createPortletGroup();

        if (items.length === 0) {
            currentGroup = createShortcutGroup();
            result.set(currentGroup.title, currentGroup);
            return result;
        }

        items.forEach((item, index: number) => {
            // If exec is empty, it's a group
            const isGroup = item.exec === '0' || !item.exec;
            const {title, type} = item;

            if (index === 0 && !isGroup) {
                currentGroup = createShortcutGroup();
                result.set(currentGroup.title, currentGroup);
            }

            if (isGroup) {
                // Initialize a new group
                currentGroup = {
                    ...item,
                    id: getGroupId(title),
                    type:
                        title === DefaultGroupsIDs.insights
                            ? HomepageGroupType.portlet
                            : HomepageGroupType.tile,
                    items: [],
                    isHidden: this.collapsedGroups.includes(title)
                };
                result.set(title, currentGroup);
            } else {
                if (type === 'p') {
                    if (!result.has(DefaultGroupsIDs.insights)) {
                        result = new Map([[DefaultGroupsIDs.insights, portletGroup], ...result]);
                        result.set(portletGroup.id, portletGroup);
                    }
                    result.get(portletGroup.title)?.items.push(item);
                } else {
                    if (currentGroup.title !== DefaultGroupsIDs.insights) {
                        result.get(currentGroup.title)?.items.push(item);
                    }
                }
            }
        });

        return result;
    }

    @action
    async addGroup(title: string, id?: string): Promise<string> {
        const isNew = !this.selectedGroup;
        const prevTitle = this.selectedGroup;

        const newGroup: Group = {
            exec: '',
            title,
            id: getGroupId(title),
            type: HomepageGroupType.tile,
            items: []
        };

        const groupKeys = [...this.groups.keys()];

        const orderedItems = groupKeys.reduce((prev, curr) => {
            const group = this.groups.get(curr);
            prev.push(group, ...group.items);

            return prev;
        }, []);

        if (this.collapsedGroups.includes(prevTitle)) {
            const updatedCollapsedGroups = this.collapsedGroups.map(group =>
                group === prevTitle ? title : group
            );
            await setCollapsedGroupsData(updatedCollapsedGroups);
        }

        const updatedData = isNew
            ? [...orderedItems, newGroup]
            : orderedItems.map(item => (prevTitle === item.title ? {...item, title} : item));

        await this.saveHomepageData(updatedData);

        if (isNew) {
            this.setScrollToParams(title, 'nearest');
        }

        return newGroup.title;
    }

    @action
    openDialog = (dialogType: HomepageDialog, flag: boolean) => {
        this.showDialog = flag;
        this.dialogType = dialogType;
    };

    @action
    closeDialog = () => {
        this.showDialog = false;
        this.dialogType = null;
        this.setSelectedGroup(null);
    };

    @action
    setSelectedGroup = (groupId: string) => {
        this.selectedGroup = groupId;
    };

    saveHomepageData = async (homepageData: Array<HomepageItemData>) => {
        await setHomepageData(homepageData);
        this.setItems(homepageData);
    };

    @action
    setPortlets = portlets => {
        this.portlets = portlets;
    };

    @action
    reloadPortlets = () => {
        this.updateHash = getUuid4();
    };
}

export const homepageStore = new HomepageStore();
